import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Music, Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (user) return <Navigate to="/admin/tableau" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/tableau");
    } catch (err) {
      setError(formatApiError(err, "Connexion impossible. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF6F0] px-4">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-stone-300 bg-white p-8 shadow-md sm:p-10">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded bg-[#B83227] text-white">
              <Music className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <p className="font-display text-xl font-bold uppercase text-[#1E1B18]">Espace membres</p>
              <p className="text-xs text-stone-500">Les Sonneurs de la Côte</p>
            </div>
          </div>
          <form onSubmit={submit} data-testid="admin-login-form" className="space-y-5">
            <div>
              <label htmlFor="admin-email" className="mb-2 block text-xs font-bold uppercase text-stone-700">E-mail</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3.5 text-sm focus:border-[#B83227] focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block text-xs font-bold uppercase text-stone-700">Mot de passe</label>
              <div className="relative">
                <input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3.5 pr-12 text-sm focus:border-[#B83227] focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-stone-400 transition-colors duration-300 hover:text-stone-700"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="mt-2 text-right">
                <Link to="/admin/mot-de-passe-oublie" data-testid="forgot-password-link" className="text-xs font-bold uppercase text-stone-500 hover:text-[#B83227]">
                  Mot de passe oublié ?
                </Link>
              </p>
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg bg-red-50 p-3 text-xs font-medium text-red-700">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full rounded bg-[#B83227] px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white shadow transition-colors duration-300 hover:bg-[#96281E] disabled:opacity-60"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center">
          <Link to="/" className="text-xs font-bold uppercase text-stone-500 hover:text-[#B83227]">
            &larr; Retour au site
          </Link>
        </p>
      </div>
    </div>
  );
}
